/**
 * Admin — System page — /admin/system
 * Worker settings with auto-optimization.
 */
import React, { useEffect, useState } from 'react';
import { AdminPageShell } from '../_components/AdminPageShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/apiFetch';
import { Loader2, CheckCircle2, Cpu, HardDrive, Zap } from 'lucide-react';

interface Setting {
  key: string;
  value: string;
  updated_at: string;
}

interface EnvAnalysis {
  cpuCores: number;
  totalRamMB: number;
  recommendations: Record<string, string>;
}

const LABELS: Record<string, string> = {
  MAX_CONCURRENT_JOBS: 'Max concurrent jobs (global)',
  POLL_INTERVAL_MS: 'Poll interval (ms)',
  CLAIM_BATCH_SIZE: 'Claim batch size',
  'extraction.MAX_CONCURRENT_JOBS': 'Extraction worker — Server1',
  'pulsar.MAX_CONCURRENT_JOBS': 'Extraction worker — Pulsar',
  'server1.MAX_CONCURRENT_JOBS': 'Main worker — Server1',
};

export const AdminSystemPage: React.FC = () => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<EnvAnalysis | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/admin/system/settings')
      .then((res) => res.json())
      .then((json) => {
        if (!cancelled) setSettings((json.data ?? []) as Setting[]);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSave(key: string, value: string) {
    setSaving(key);
    try {
      const res = await apiFetch('/api/admin/system/settings', {
        method: 'POST',
        body: JSON.stringify({ key, value }),
      });
      if (res.ok) {
        setSettings((prev) =>
          prev.map((s) => (s.key === key ? { ...s, value } : s))
        );
        setFeedback(`Saved ${LABELS[key] ?? key} = ${value}`);
        setTimeout(() => setFeedback(null), 3000);
      }
    } catch {
      // ignore
    } finally {
      setSaving(null);
    }
  }

  async function handleAnalyze() {
    setAnalysisLoading(true);
    try {
      const res = await apiFetch('/api/admin/system/analyze-env');
      const json = await res.json();
      setAnalysis(json.data as EnvAnalysis);
    } catch {
      // ignore
    } finally {
      setAnalysisLoading(false);
    }
  }

  async function applyAllRecommended() {
    if (!analysis) return;
    for (const [key, value] of Object.entries(analysis.recommendations)) {
      if (key === 'NODE_HEAP_MB') continue; // requires PM2 restart
      await handleSave(key, value);
    }
  }

  if (loading) {
    return (
      <AdminPageShell title="System">
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell title="System">
      <div className="space-y-6">
        {feedback && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-2 text-sm">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            {feedback}
          </div>
        )}

        {/* Worker Settings */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-5 py-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Worker Settings
            </h2>
            <p className="text-sm text-gray-500">
              Changes take effect within 60 seconds — no restart needed.
            </p>
          </div>
          <div className="px-5 py-4 space-y-3">
            {settings.map((s) => (
              <div key={s.key} className="flex items-center gap-3">
                <Label className="w-48 shrink-0 text-sm text-gray-700">
                  {LABELS[s.key] ?? s.key}
                </Label>
                <Input
                  type="number"
                  value={s.value}
                  onChange={(e) =>
                    setSettings((prev) =>
                      prev.map((x) =>
                        x.key === s.key ? { ...x, value: e.target.value } : x
                      )
                    )
                  }
                  className="w-32"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSave(s.key, s.value)}
                  disabled={saving === s.key}
                >
                  {saving === s.key ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    'Save'
                  )}
                </Button>
                <span className="text-xs text-gray-400">
                  Updated: {new Date(s.updated_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Environment Analysis */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-5 py-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Environment Analysis
            </h2>
            <p className="text-sm text-gray-500">
              Detect CPU and RAM to recommend optimal settings.
            </p>
          </div>
          <div className="px-5 py-4 space-y-3">
            <Button
              variant="outline"
              onClick={handleAnalyze}
              disabled={analysisLoading}
            >
              {analysisLoading ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-1" />
              )}
              Analyze Environment
            </Button>

            {analysis && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                  <Cpu className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">
                    <span className="text-gray-500">CPU:</span>{' '}
                    <strong>{analysis.cpuCores} cores</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                  <HardDrive className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">
                    <span className="text-gray-500">RAM:</span>{' '}
                    <strong>
                      {(analysis.totalRamMB / 1024).toFixed(1)} GB
                    </strong>
                  </span>
                </div>

                <div className="col-span-2 mt-2 space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    Recommended:
                  </p>
                  {Object.entries(analysis.recommendations)
                    .filter(([k]) => k !== 'NODE_HEAP_MB')
                    .map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-1.5"
                      >
                        <span className="text-sm text-blue-700">
                          {LABELS[key] ?? key}
                        </span>
                        <span className="text-sm font-bold text-blue-800">
                          {value}
                        </span>
                      </div>
                    ))}
                  {analysis.recommendations.NODE_HEAP_MB && (
                    <div className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-1.5">
                      <span className="text-sm text-amber-700">
                        Node heap (requires PM2 restart)
                      </span>
                      <span className="text-sm font-bold text-amber-800">
                        {analysis.recommendations.NODE_HEAP_MB} MB
                      </span>
                    </div>
                  )}
                  <Button
                    size="sm"
                    onClick={applyAllRecommended}
                    className="w-full mt-2"
                  >
                    <Zap className="w-3.5 h-3.5 mr-1" />
                    Apply All Recommended
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminPageShell>
  );
};
